import classNames from 'classnames'
import React from 'react'
import { Bar, Cell, ComposedChart, Line, Rectangle, RectangleProps, Tooltip, YAxis } from 'recharts'

import { ColorVariant } from '../../../components'
import {
  BAR_GAP,
  BAR_WIDTH,
  BAR_WIDTH_NARROW,
  CHART_HEIGHT,
  CHART_MARGINS,
  CURSOR_STYLE,
  DividendValueProperty,
  EXTRA_WIDTH,
  LABELS_MARGIN,
  LABELS_WIDTH,
  TICK_STYLE,
  TOOLTIP_WRAPPER_STYLE,
  TRANSPARENT_CURSOR,
} from '../constants'
import { DividendsDataType } from '../types'
import { getColor, tickFormatter } from '../utils'
import ChartTooltip from './ChartTooltip'

interface CustomCursorProps extends Partial<Omit<RectangleProps, 'points'>> {
  barWithGapWidth: number
  isActive: boolean
  points?: [{ x: number }]
}
function CustomCursor({ barWithGapWidth, points, ...props }: CustomCursorProps) {
  const x = points![0].x - barWithGapWidth / 2
  return (
    <Rectangle {...props} x={x} width={barWithGapWidth} {...(props.isActive ? CURSOR_STYLE : TRANSPARENT_CURSOR)} />
  )
}

export type Props = {
  annualData: DividendsDataType[]
  isCompact: boolean
}

type ChartYearData = { year: string; isEstimate: boolean } & Record<DividendsDataType['id'], number | null>

const CHART_GRID_COMMON_PROPS = 'absolute right-8 border-t border-dashed border-secondary'

export function DividendsChart({ annualData, isCompact }: Props) {
  const [isActive, setIsActive] = React.useState(false)
  const [activeBarKey, setActiveBarKey] = React.useState('')
  const [reverseTooltipDirection, setReverseTooltipDirection] = React.useState(true)
  const columnsCount = annualData[0].values.length
  const barWidth = isCompact ? BAR_WIDTH_NARROW : BAR_WIDTH
  const { estimateColumnsCount, hasBarChart } = React.useMemo(
    () => ({
      estimateColumnsCount: annualData[0].values.filter(({ isEstimate }) => isEstimate).length,
      hasBarChart: annualData.some(({ id, isSelected }) => id === DividendValueProperty.Amount && isSelected),
    }),
    [annualData]
  )
  const chartData = React.useMemo(() => {
    const data: Record<string, ChartYearData> = {}
    annualData.forEach(({ id, values }) => {
      values.forEach(({ value, year, isEstimate }) => {
        data[year] = { ...data[year], year, isEstimate, [id]: value }
      })
    })
    return Object.values(data)
  }, [annualData])

  const visibleLeftAxes = React.useMemo(() => annualData.slice(1).filter((group) => group.isSelected), [annualData])

  const chartWidth =
    columnsCount * barWidth + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH + LABELS_WIDTH * 2 * visibleLeftAxes.length
  const estimateAreaWidth = estimateColumnsCount * barWidth + estimateColumnsCount * BAR_GAP
  const barLessChartYMargin = barWidth / 2 + BAR_GAP / 2

  const chartGridCommonLeftProps = {
    'left-0': visibleLeftAxes.length === 0,
    'left-14': visibleLeftAxes.length === 1,
    'left-28': visibleLeftAxes.length === 2,
  }

  return (
    <div className="flex w-full justify-end">
      <div className="relative flex justify-end" style={{ width: visibleLeftAxes.length ? chartWidth : '100%' }}>
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-0', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-1/4', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-1/2', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-3/4', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'bottom-0', chartGridCommonLeftProps)} />
        <div
          className="pointer-events-none absolute bottom-0 right-0 z-10 h-full bg-stripes text-gray-50 mix-blend-multiply dark:text-gray-100"
          style={{ width: estimateAreaWidth, right: LABELS_WIDTH }}
        />

        <div
          /**
           * Fix a problem with chart tooltip not hiding correctly on mouse leave
           * See: https://github.com/recharts/recharts/issues/2726
           */
          onMouseEnter={() => {
            setIsActive(true)
          }}
          onMouseLeave={() => {
            setIsActive(false)
          }}
        >
          <ComposedChart
            width={chartWidth}
            height={CHART_HEIGHT}
            data={chartData}
            margin={
              hasBarChart ? CHART_MARGINS : { ...CHART_MARGINS, left: barLessChartYMargin, right: barLessChartYMargin }
            }
            stackOffset="sign" // because we can have negative values
            onMouseMove={(_, mouseEvent) => {
              setReverseTooltipDirection(mouseEvent.clientX > window.innerWidth / 2)
            }}
          >
            {annualData.map((row, index) => {
              const axisId = index > 0 ? `left${index}` : 'right'
              const orientation = index > 0 ? 'left' : 'right'
              const tickMargin = index > 0 ? (hasBarChart ? 0 : barLessChartYMargin) : LABELS_MARGIN
              const axisWidth = index > 0 ? LABELS_WIDTH * 2 : LABELS_WIDTH

              // Hide unselected axes but always keep the right axis
              if (!row.isSelected && index > 0) return null

              return (
                <YAxis
                  key={index}
                  orientation={orientation}
                  yAxisId={axisId}
                  tick={TICK_STYLE}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={tickFormatter}
                  tickMargin={tickMargin}
                  width={axisWidth}
                  interval="preserveStartEnd"
                  label={
                    index === 0
                      ? undefined
                      : {
                          value: row.label,
                          position: 'insideLeft',
                          angle: -90,
                          dx: hasBarChart ? 5 : -barLessChartYMargin + 5,
                          style: { ...TICK_STYLE, textAnchor: 'middle' },
                        }
                  }
                />
              )
            })}
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              position={{ y: 0 }}
              allowEscapeViewBox={{ x: true }}
              reverseDirection={{ x: reverseTooltipDirection }}
              offset={40}
              isAnimationActive={false}
              cursor={<CustomCursor isActive={isActive} barWithGapWidth={barWidth + BAR_GAP} />}
              content={({ payload }) => <ChartTooltip isActive={isActive} activeKey={activeBarKey} payload={payload} />}
            />
            {annualData.map(({ id, label, isSelected, color }, rowIndex) => {
              if (!isSelected) return null

              const axisId = rowIndex > 0 ? `left${rowIndex}` : 'right'

              if (id === DividendValueProperty.Amount) {
                return (
                  <Bar
                    key={id}
                    name={label}
                    dataKey={id}
                    fill={getColor(color)}
                    yAxisId={axisId}
                    isAnimationActive={false}
                    onMouseEnter={() => {
                      setActiveBarKey(label)
                    }}
                    onMouseLeave={() => {
                      setActiveBarKey('')
                    }}
                  >
                    {chartData.map((row, index) => (
                      <Cell
                        key={index}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - number[] should also be allowed type for radius, but it's not
                        radius={[2, 2, 0, 0]}
                        fill={getColor(row.isEstimate ? ColorVariant.gray : color)}
                      />
                    ))}
                  </Bar>
                )
              }

              return (
                <Line
                  key={id}
                  name={label}
                  dataKey={id}
                  yAxisId={axisId}
                  type="linear"
                  dot={{ strokeWidth: 0 }}
                  activeDot={{
                    strokeWidth: 1,
                    stroke: getColor(color),
                    onMouseEnter: () => {
                      setActiveBarKey(label)
                    },
                    onMouseLeave: () => {
                      setActiveBarKey('')
                    },
                  }}
                  stroke={getColor(color)}
                  fill={getColor(color)}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              )
            })}
          </ComposedChart>
        </div>
      </div>
    </div>
  )
}
